import { useEffect, useState, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { Calendar, ChevronRight, DollarSign, FileText, Plus, Sparkles, Wrench } from 'lucide-react'

import { useCenterStore, useUserStore } from '@caradasstore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import adasIntelligenceLogo from '../../assets/images/adas-intelligence-horizontal-logo.svg'
import { REDIRECT_URLS } from '../../constants'
import { isEmpty } from '../../utils/utils'
import { formatExistingTimeOrReturn } from 'utils/utils'
import { getUserList, getDashboardDetails, getCalibrationCenter } from '../../api/api'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { useModuleAccess } from '@hooks/useModuleAccess'

const KpiCard = ({ label, value, sublabel, onClick }) => (
  <button
    type='button'
    onClick={onClick}
    className={`group relative flex w-full flex-col items-start gap-2 overflow-hidden rounded-lg border border-[#EBEDF0] bg-white px-5 py-4 text-left shadow-sm transition-all ${
      onClick ? 'hover:-translate-y-0.5 hover:shadow-md' : 'cursor-default'
    }`}
  >
    <span className='absolute left-0 top-0 h-full w-[3px] bg-[#1F6FEB]' />
    <span className='text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]'>{label}</span>
    <span className='text-[36px] font-bold leading-none text-[#0A1628]'>{value}</span>
    <span className='text-[13px] text-[#6B7280]'>{sublabel}</span>
  </button>
)

const ScheduleRow = ({ time, vehicle, name, ro, quote, onClick }) => (
  <button
    type='button'
    onClick={onClick}
    className='flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-[#F8FAFC]'
  >
    <span className='w-[80px] flex-none text-[13px] font-bold text-[#1F6FEB]'>{time}</span>
    <div className='min-w-0 flex-1'>
      <div className='truncate text-[14px] font-bold text-[#0A1628]'>{vehicle}</div>
      {name && <div className='truncate text-[13px] text-[#6B7280]'>{name}</div>}
    </div>
    <div className='hidden flex-none text-right sm:block'>
      {ro && (
        <div className='text-[11px] text-[#6B7280]'>
          <span className='font-semibold uppercase tracking-wide'>RO </span>
          <span className='font-mono text-[#0A1628]'>#{ro}</span>
        </div>
      )}
      {quote && (
        <div className='text-[11px] text-[#6B7280]'>
          <span className='font-semibold uppercase tracking-wide'>QUOTE </span>
          <span className='font-mono text-[#0A1628]'>#{quote}</span>
        </div>
      )}
    </div>
  </button>
)

const QuickActionRow = ({ icon: Icon, title, subtitle, onClick }) => (
  <button
    type='button'
    onClick={onClick}
    className='flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors hover:bg-[#F8FAFC]'
  >
    <span className='inline-flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-[#E8EFFF]'>
      <Icon className='h-4 w-4 text-[#1F6FEB]' />
    </span>
    <div className='min-w-0 flex-1'>
      <div className='truncate text-[14px] font-bold text-[#0A1628]'>{title}</div>
      {subtitle && <div className='truncate text-[12px] text-[#6B7280]'>{subtitle}</div>}
    </div>
    <ChevronRight className='h-4 w-4 flex-none text-[#9CA3AF]' />
  </button>
)

export const HomePage = () => {
  const history = useHistory()

  const ccId = useCenterStore((s) => s.ccId)
  const centerDetail = useCenterStore((s) => s.centerDetail)
  const setCenterDetail = useCenterStore((s) => s.setCenterDetail)
  const setUserList = useCenterStore((s) => s.setUserList)
  const userEmail = useUserStore((s) => s.userEmail)
  const setCurrentUser = useUserStore((s) => s.setCurrentUser)
  const setLoading = useLoadingStore((s) => s.setLoading)

  const [dashboardData, setDashboardData] = useState()
  const currentUser = useUserStore((s) => s.currentUser)
  // A client user sees the same Home, counted for its own client only: the backend scopes the
  // dashboard figures and today's appointments.
  const isReadOnly = useIsReadOnly()
  const canUse = useModuleAccess()
  const isRoleKnown = !!currentUser

  const fetchCalibrationCenter = useCallback(async () => {
    setLoading(true)
    try {
      // The axios error interceptor swallows handled failures and resolves to undefined
      // (callers must use optional chaining — see fetchUserList below). Without this guard
      // a failed center fetch crashed here destructuring `status`, and left centerDetail
      // unset downstream (e.g. EmailForm).
      const response = await getCalibrationCenter({ token: '', ccid: ccId })
      if (response?.status === 200) setCenterDetail(response.data)
    } finally {
      setLoading(false)
    }
  }, [ccId, setCenterDetail, setLoading])

  const fetchUserList = useCallback(async () => {
    const response = await getUserList({ ccid: centerDetail.id || ccId })
    if (response?.status === 200) {
      setUserList(response.data)
      if (userEmail) {
        const user = response.data.find((item) => item.email?.toLowerCase() === userEmail?.toLowerCase())
        if (user) setCurrentUser(user)
      }
    }
  }, [centerDetail.id, ccId, setUserList, setCurrentUser, userEmail])

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await getDashboardDetails({ ccId })
      setDashboardData(response.data)
    } catch (err) {
      // intentionally swallowed — dashboard tiles fall back to 0
    }
  }, [ccId])

  useEffect(() => {
    if (ccId && isEmpty(centerDetail)) {
      fetchCalibrationCenter()
    }
  }, [ccId, centerDetail, fetchCalibrationCenter])

  useEffect(() => {
    if (ccId) {
      fetchUserList()
    }
  }, [ccId, centerDetail.id, fetchUserList])

  // Waits for the role, so the figures are fetched once for the right audience.
  useEffect(() => {
    if (ccId && isRoleKnown) {
      fetchDashboard()
    }
  }, [ccId, centerDetail.id, isRoleKnown, fetchDashboard])

  const go = (url) => () => history.push(url)

  // Package gating (CAR-3710): a tile or shortcut into a section the center's plan does not
  // include is dropped (same rule as the sidebar), not left to land on the upgrade notice.
  const inPlan = (item) => canUse(item.module)

  const kpis = [
    {
      label: 'NEW',
      value: dashboardData?.newAiReports ?? 0,
      sublabel: 'AI Reports',
      module: 'ai_brief',
      onClick: go('/adas-intelligence')
    },
    {
      label: "TODAY'S ACTIVITY",
      value: dashboardData?.appointments?.length || 0,
      sublabel: 'Appointments',
      module: 'scheduler',
      onClick: go('/scheduler')
    },
    {
      label: 'AWAITING RESPONSE',
      value: dashboardData?.openQuotes ?? 0,
      sublabel: 'Open Quotes',
      module: 'quotes',
      onClick: go('/quotes')
    },
    {
      label: 'ACTIVE JOBS',
      value: dashboardData?.openWorkOrders ?? 0,
      sublabel: 'Open Work Orders',
      module: 'work_orders',
      onClick: go('/workorders')
    },
    {
      label: 'READY TO CLOSE',
      value: dashboardData?.openInvoices ?? 0,
      sublabel: 'Open Invoices',
      module: 'invoicing',
      onClick: go('/invoices')
    }
  ].filter(inPlan)

  const appointments = dashboardData?.appointments || []

  // The two create shortcuts lead to routes a client user cannot open, so they are
  // dropped rather than left to bounce off the forbidden page.
  const quickActions = [
    ...(isReadOnly
      ? []
      : [
          {
            icon: Plus,
            title: 'New Quote',
            subtitle: 'Start from VIN or scan',
            module: 'quotes',
            onClick: go(REDIRECT_URLS.NEW_QUOTE)
          },
          {
            icon: Wrench,
            title: 'Create Work Order',
            subtitle: 'Convert quote or build new',
            module: 'work_orders',
            onClick: go(REDIRECT_URLS.NEW_WORK_ORDER)
          }
        ]),
    {
      icon: Sparkles,
      title: 'Review AI Scrubs',
      subtitle: `${dashboardData?.newAiReports ?? 0} new awaiting review`,
      module: 'ai_brief',
      onClick: go('/adas-intelligence')
    },
    {
      icon: Calendar,
      title: 'Open Scheduler',
      subtitle: 'Today and the week ahead',
      module: 'scheduler',
      onClick: go('/scheduler')
    },
    {
      icon: FileText,
      title: 'View Quotes',
      subtitle: `${dashboardData?.openQuotes ?? 0} open`,
      module: 'quotes',
      onClick: go('/quotes')
    },
    ...(isReadOnly
      ? [
          {
            icon: Wrench,
            title: 'View Work Orders',
            subtitle: `${dashboardData?.openWorkOrders ?? 0} open`,
            module: 'work_orders',
            onClick: go('/workorders')
          }
        ]
      : []),
    {
      icon: DollarSign,
      title: 'View Invoices',
      subtitle: `${dashboardData?.openInvoices ?? 0} open`,
      module: 'invoicing',
      onClick: go('/invoices')
    }
  ].filter(inPlan)

  return (
    <div className='mx-auto max-w-[1400px] py-6'>
      <div>
        {/* Header */}
        <div className='mb-4 flex items-center justify-between'>
          <h1 className='m-0 leading-none'>
            <img src={adasIntelligenceLogo} alt='ADAS Intelligence' className='h-12 w-auto' />
          </h1>
        </div>

        {/* KPI cards */}
        <div className='mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5'>
          {kpis.map((kpi) => (
            <KpiCard key={kpi.label} {...kpi} />
          ))}
        </div>

        {/* Schedule + Quick Actions */}
        <div className='grid grid-cols-1 gap-4 lg:grid-cols-5'>
          {/* Today's Schedule */}
          <div className='overflow-hidden rounded-lg border border-[#EBEDF0] bg-white shadow-sm lg:col-span-3'>
            <div className='px-5 py-4'>
              <h2 className='text-[16px] font-bold text-[#0A1628]'>Today&apos;s Schedule</h2>
            </div>
            <div className='divide-y divide-[#EBEDF0] border-t border-[#EBEDF0]'>
              {appointments.length === 0 ? (
                <div className='px-5 py-10 text-center text-[13px] text-[#6B7280]'>No appointments scheduled for today.</div>
              ) : (
                appointments.map((item, i) => {
                  const startTime = formatExistingTimeOrReturn(
                    item.appointment_date,
                    item.start_time,
                    item.organisation?.timezone_at || 'UTC',
                    item.duration
                  )?.startTime
                  const vehicle = [item.vehicle?.year, item.vehicle?.make, item.vehicle?.model].filter(Boolean).join(' ')
                  return (
                    <ScheduleRow
                      key={item.id || i}
                      time={startTime}
                      vehicle={vehicle || '—'}
                      name={item.client?.full_name}
                      ro={item.work_order_reference || item.ro_number}
                      quote={item.quote_reference}
                      onClick={item.id ? go(`/scheduler?event=${item.id}`) : undefined}
                    />
                  )
                })
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className='overflow-hidden rounded-lg border border-[#EBEDF0] bg-white shadow-sm lg:col-span-2'>
            <div className='px-5 py-4'>
              <h2 className='text-[16px] font-bold text-[#0A1628]'>Quick Actions</h2>
            </div>
            <div className='border-t border-[#EBEDF0] p-2'>
              {quickActions.map((action) => (
                <QuickActionRow key={action.title} {...action} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
